import React, { useEffect, useRef, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";

import { faPlusCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ButtonVariant } from "Constant/button";
import Form from "react-bootstrap/Form";
import { useSelector } from "react-redux";
import ClipLoader from "react-spinners/ClipLoader";

import useHostFooterAction from "components/organisms/CreationModal/hooks/useHostFooterAction";

import {
    getAllSourceMapping,
    getCrmSourceMappingFields,
    sendCrmSourceUpdate,
} from "../helpers/Sources/createSource";
import MappingEmptyState from "./atoms/MappingEmptyState";
import StandartButton from "./atoms/StandartButton";
import { getDefaultValues } from "./CrmSourceMappingArea.helpers";
import SwalAlertComp from "./SwalAlert";

/**
 * Field mapping editor shared by the CRM source flows.
 * Pass `registerFooterAction` (+ `hideSaveButton`) to move the Save button
 * into a host-owned footer; without them the inline Save button renders.
 */
const CrmSourceMappingArea = (props) => {
    const { registerFooterAction, hideSaveButton = false } = props;
    const language = useSelector((state) => state.languagesState.languages);
    const [form, setForm] = useState({});
    const [mappings, setMappings] = useState([]);
    const [sourceFields, setSourceFields] = useState([]);
    const [allMergedFields, setAllMergedFields] = useState([]);
    const [requiredMergedFields, setRequiredMergedFields] = useState([]);
    const [noneSelectFields, setNoneSelectFields] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [loaderStatus, setLoaderStatus] = useState(false);
    const [isButtonLoading, setIsButtonLoading] = useState(false);
    const [checkErrorStatus, setCheckErrorStatus] = useState(false);
    const [prefix, setPrefix] = useState("");
    const [checkReconnect, setCheckReconnect] = useState(false);
    const [mandotaryFields, setMandotaryFields] = useState([]);
    const [hasNoMapping, setHasNoMapping] = useState(false);
    const scrollAreaRef = useRef(null);
    const shouldScrollToBottom = useRef(false);

    useEffect(() => {
        componentRender();
    }, []);

    const componentRender = async () => {
        setLoaderStatus(true);
        let response = await getCrmSourceMappingFields(
            props?.sourceType,
            props?.sourceId,
        );
        if (response.status) {
            if (response?.data?.url !== undefined) {
                setLoaderStatus(false);
                setCheckReconnect(true);
            } else {
                let mapping_fields_all =
                    response?.data?.field_data?.meta?.system_fields?.[0];
                let mapping_fields_required =
                    response?.data?.field_data?.meta?.system_fields?.[1];
                let event_prefix = response?.data?.event_prefix;
                mapping_fields_all = Object.keys(mapping_fields_all).map(
                    (x) => ({ value: x, label: mapping_fields_all[x] }),
                );
                mapping_fields_required = Object.keys(
                    mapping_fields_required,
                ).map((x) => ({ value: x, label: mapping_fields_required[x] }));
                const sourceFieldsLabelValue =
                    response?.data?.field_data?.fields.map((x) => ({
                        label: x.display_value,
                        value: x.api_value,
                    }));
                let requiredValues = {};
                mapping_fields_required.forEach((item) => {
                    requiredValues[item.value] = "";
                });
                setSourceFields(sourceFieldsLabelValue);
                setAllMergedFields(mapping_fields_all);
                setNoneSelectFields(mapping_fields_all);
                setPrefix(event_prefix);
                if (props?.isEditMapping) {
                    await isEditMappingCheck(
                        props?.isEditMapping,
                        props?.sourceId,
                        requiredValues,
                        mapping_fields_required,
                        mapping_fields_all,
                    );
                } else {
                    setRequiredMergedFields(mapping_fields_required);
                    setForm(
                        createRequiredValues(
                            requiredValues,
                            props?.sourceType,
                            sourceFieldsLabelValue,
                        ),
                    );
                }
                setLoaderStatus(false);
            }
        } else {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response.message && response.message,
                icon: "error",
            });
        }
    };

    const createRequiredValues = (object, type, fields) => {
        let newObject = {};
        let defaultValues = getDefaultValues(type);
        Object.keys(object).forEach((key) => {
            if (
                Object.keys(defaultValues).includes(key) &&
                fields.find((x) => x.value === defaultValues[key]) !== undefined
            ) {
                newObject[key] = {
                    label: fields.find((x) => x.value === defaultValues[key])
                        ?.label,
                    value: fields.find((x) => x.value === defaultValues[key])
                        ?.value,
                };
            } else {
                newObject[key] = {
                    label: "None",
                    value: "none",
                };
            }
        });
        return newObject;
    };

    const setField = (field, value) => {
        setForm({
            ...form,
            [field]: value,
        });
    };

    const setMappingProperty = (index, value) => {
        const updatedItems = [...mappings];
        updatedItems[index] = { ...updatedItems[index], property: value };
        setMappings(updatedItems);
    };

    const setMappingValue = (index, value, label) => {
        const updatedItems = [...mappings];
        updatedItems[index] = {
            ...updatedItems[index],
            value: {
                value: value,
                label: label,
            },
        };
        setMappings(updatedItems);
    };

    const addNewRow = () => {
        let obj = { property: null, value: null };
        shouldScrollToBottom.current = true;
        setMappings([...mappings, obj]);
    };

    useEffect(() => {
        if (!shouldScrollToBottom.current) {
            return;
        }
        shouldScrollToBottom.current = false;
        const dialog = scrollAreaRef.current?.closest('[role="dialog"]');
        let el = scrollAreaRef.current;
        while (el && el !== document.body && el !== document.documentElement) {
            if (dialog && !dialog.contains(el)) {
                break;
            }
            if (el.scrollHeight > el.clientHeight + 1) {
                el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
                return;
            }
            el = el.parentElement;
        }
    }, [mappings]);

    const deleteRow = (i, property) => {
        let updatedItems = [...mappings];
        updatedItems = updatedItems.filter((item, index) => index !== i);
        setMappings(updatedItems);
    };

    const saveMapping = async () => {
        setIsButtonLoading(true);
        setCheckErrorStatus(true);
        const localForm = {};
        Object.keys(form).forEach((formKey) => {
            localForm[formKey] = form[formKey]?.value;
        });
        let requiredRows = { ...localForm };
        let mappingRows = { ...localForm };
        mappings.forEach((item) => {
            mappingRows[item.property?.value] = item?.value?.value;
        });
        if (
            [...Object.keys(requiredRows)].filter((x) => x?.label === "")
                .length === 0 &&
            [...Object.keys(mappingRows)].filter((x) => x?.label === "")
                .length === 0
        ) {
            let response = await sendCrmSourceUpdate(
                { mappings: mappingRows, event_prefix: prefix },
                props?.sourceId,
                props?.sourceType,
            );
            if (response.status) {
                if (props?.mappingSuccess) {
                    props.mappingSuccess(response);
                }
                if (props?.closeModal) {
                    props?.closeModal();
                }
            } else {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: response.message && response.message,
                    icon: "error",
                });
            }
        } else {
            SwalAlertComp({
                title: language?.mapping_modal?.warning ?? "Warning!",
                message:
                    language?.mapping_modal?.rows_empty ??
                    "There are empty mappings",
                icon: "error",
            });
        }
        setIsButtonLoading(false);
    };

    const checkError = (val) => {
        if (checkErrorStatus) {
            return val === "" || val === null || val === undefined;
        } else {
            return false;
        }
    };

    useEffect(() => {
        let mappingRows = { ...form };
        mappings.forEach((item) => {
            mappingRows[item.property] = item.value;
        });
        let opts = [...allMergedFields];
        let using_opts = Object.keys(mappingRows);
        setNoneSelectFields(
            opts.filter((x) => using_opts.indexOf(x.value) === -1),
        );
    }, [mappings, form]);

    const mergeSelectFields = (val) => {
        const mergedField = [...allMergedFields].find((x) => x.value === val);

        return mergedField !== undefined
            ? [...noneSelectFields, mergedField]
            : [...noneSelectFields];
    };

    const getMergedSelectFields = (val) => {
        let _mergeSelectFields = [...mergeSelectFields(val)];
        let _mappings = [...mappings];
        _mappings = _mappings.filter((y) => y.property !== val);
        return _mergeSelectFields.filter(
            (x) => _mappings.filter((y) => y.property === x.value).length === 0,
        );
    };

    const isEditMappingCheck = async (
        editMappingStatu,
        sourceId,
        requiredValues,
        reqFields,
        allMergedFields,
    ) => {
        if (editMappingStatu) {
            setLoaderStatus(true);
            let res = await getAllSourceMapping(sourceId);
            if (res.status) {
                let sourceMappings = res?.data;
                let reqList = [];
                let fieldList = [];
                if (
                    !sourceMappings?.mappings?.length &&
                    !sourceMappings?.has_missing_mandatory
                ) {
                    setHasNoMapping(true);
                }
                sourceMappings?.mappings.forEach((item) => {
                    if (Object.keys(requiredValues).indexOf(item.key) > -1) {
                        let reqObj = reqFields.find(
                            (x) => x.value === item.key,
                        );
                        if (reqObj !== undefined) {
                            reqList.push(reqObj);
                            form[item.key] = {
                                label: item.display_value,
                                value: item.api_value,
                            };
                        }
                    } else {
                        fieldList.push({
                            property: {
                                label:
                                    allMergedFields?.find(
                                        (field) => field?.value === item?.key,
                                    )?.label || item.key,
                                value: item.key,
                            },
                            value: {
                                value: item.api_value,
                                label: item.display_value,
                            },
                        });
                    }
                });
                if (sourceMappings?.has_missing_mandatory) {
                    setMandotaryFields(sourceMappings?.missing_mandatory_keys);
                    sourceMappings?.missing_mandatory_keys?.forEach((key) => {
                        fieldList.push({
                            property: {
                                label:
                                    reqFields?.find(
                                        (field) => field?.value === key,
                                    )?.label || key,
                                value: key,
                            },
                            value: {
                                value: "none",
                                label: "None",
                            },
                        });
                    });
                }
                setRequiredMergedFields(reqList);
                setMappings(fieldList);
                setLoaderStatus(false);
            } else {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: res.message && res.message,
                    icon: "error",
                });
                setLoaderStatus(false);
            }
        }
    };

    useEffect(() => {
        if (mappings && requiredMergedFields) {
            setRowCount(mappings.length + requiredMergedFields.length);
        }
    }, [mappings, requiredMergedFields]);

    const isSaveLoading = isButtonLoading || !!props?.isSaveButtonLoading;

    useHostFooterAction(registerFooterAction, "mapping", {
        label: language?.buttons?.save ?? "Save",
        onSubmit: saveMapping,
        isLoading: isSaveLoading,
        hidden: loaderStatus || checkReconnect,
    });

    return (
        <div>
            {checkReconnect && (
                <div className="mapping-reconnect-area">
                    <Button
                        onClick={props?.handleReconnect}
                        variant="contained"
                        color="error"
                        size="small"
                    >
                        {language?.buttons?.reconnect ?? "Reconnect"}
                    </Button>
                </div>
            )}
            <Box
                ref={scrollAreaRef}
                className={`modal-body position-relative z-1 tour-source-mapping-area`}
            >
                {loaderStatus && (
                    <div className="circle-loader">
                        <ClipLoader color={"#000"} size={40} />
                    </div>
                )}
                {!loaderStatus && !checkReconnect && hasNoMapping && (
                    <MappingEmptyState
                        title={
                            language?.source_modal?.no_mapping_title ??
                            "No mapping configured"
                        }
                        subtitle={
                            language?.source_modal?.no_mapping_subtitle ??
                            "This source has no field mappings yet. Use the form below to set one up."
                        }
                    />
                )}
                <FormControl className="mb-3" style={{ width: "100%" }}>
                    <TextField
                        name="prefix"
                        label={
                            language?.source_modal?.prefix ??
                            "Event Name Prefix"
                        }
                        variant="outlined"
                        onChange={(e) => setPrefix(e.target.value)}
                        placeholder={
                            language?.source_modal?.enter_prefix ??
                            "Enter Event Name Prefix"
                        }
                        value={prefix}
                        required
                    />
                </FormControl>
                {requiredMergedFields.length > 0 &&
                    requiredMergedFields.map((item) => (
                        <div className="row d-flex justify-content-start my-3">
                            <div className="col-5">
                                <Form.Group
                                    className="mb-3"
                                    controlId="exampleForm.ControlTextarea1"
                                >
                                    <FormControl
                                        style={{ width: "100%" }}
                                        error={checkError(form[item.value])}
                                    >
                                        <Autocomplete
                                            label={item.label}
                                            onChange={(event, selectedOpt) => {
                                                setField(
                                                    item.value,
                                                    selectedOpt,
                                                );
                                                setCheckErrorStatus(true);
                                            }}
                                            value={{
                                                label: form[item.value]?.label,
                                                value: form[item.value]?.value,
                                            }}
                                            required
                                            options={sourceFields}
                                            getOptionLabel={(option) =>
                                                option.label || ""
                                            }
                                            freeSolo
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    error={checkError(
                                                        form[item.value],
                                                    )}
                                                    label={item.label}
                                                />
                                            )}
                                        ></Autocomplete>
                                    </FormControl>
                                </Form.Group>
                            </div>
                            <div className="col-1 d-flex justify-content-center align-items-center">
                                <FontAwesomeIcon icon={faPlusCircle} />
                            </div>
                            <div className="col-5">
                                <FormControl style={{ width: "100%" }}>
                                    <TextField
                                        variant="outlined"
                                        placeholder={item.label}
                                        disabled
                                        style={{ backgroundColor: "#efefef" }}
                                    />
                                </FormControl>
                            </div>
                            <div className="col-1" />
                        </div>
                    ))}
                {mappings.length > 0 &&
                    mappings.map((item, index) => (
                        <div className="row d-flex justify-content-between my-3">
                            <div className="col-5">
                                <Form.Group
                                    className="mb-3"
                                    controlId="exampleForm.ControlTextarea1"
                                >
                                    <FormControl style={{ width: "100%" }}>
                                        <Autocomplete
                                            label={
                                                language?.mapping_modal
                                                    ?.value_title ?? "Property"
                                            }
                                            value={item?.value}
                                            onChange={(event, selectedOpt) => {
                                                setMappingValue(
                                                    index,
                                                    selectedOpt?.value,
                                                    selectedOpt?.label,
                                                );
                                            }}
                                            options={sourceFields}
                                            getOptionLabel={(option) =>
                                                option?.label || ""
                                            }
                                            freeSolo
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={
                                                        language?.mapping_modal
                                                            ?.value ?? "Value"
                                                    }
                                                />
                                            )}
                                        ></Autocomplete>
                                    </FormControl>
                                </Form.Group>
                            </div>
                            <div className="col-1 d-flex justify-content-center align-items-center">
                                <FontAwesomeIcon icon={faPlusCircle} />
                            </div>
                            <div className="col-5">
                                <Form.Group
                                    className="mb-3"
                                    controlId="exampleForm.ControlTextarea1"
                                >
                                    <FormControl style={{ width: "100%" }}>
                                        {mandotaryFields?.includes(
                                            item?.property?.value,
                                        ) ? (
                                            <TextField
                                                variant="outlined"
                                                placeholder={
                                                    item.property.label
                                                }
                                                disabled
                                                style={{
                                                    backgroundColor: "#efefef",
                                                }}
                                            />
                                        ) : (
                                            <Autocomplete
                                                label={
                                                    language?.mapping_modal
                                                        ?.property_title ??
                                                    "Value"
                                                }
                                                value={item.property}
                                                onChange={(
                                                    event,
                                                    selectedOpt,
                                                ) => {
                                                    setMappingProperty(
                                                        index,
                                                        selectedOpt,
                                                    );
                                                }}
                                                options={getMergedSelectFields(
                                                    item.property,
                                                )}
                                                getOptionLabel={(option) =>
                                                    option.label
                                                }
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label={
                                                            language
                                                                ?.mapping_modal
                                                                ?.property ??
                                                            "Property"
                                                        }
                                                    />
                                                )}
                                            ></Autocomplete>
                                        )}
                                    </FormControl>
                                </Form.Group>
                            </div>
                            <div className="col-1 d-flex justify-content-center align-items-center mb-2">
                                <StandartButton
                                    iconButton
                                    variant={ButtonVariant.DANGER}
                                    onClick={() => {
                                        deleteRow(index, item.property);
                                    }}
                                >
                                    <DeleteOutlineIcon />
                                </StandartButton>
                            </div>
                        </div>
                    ))}
                {noneSelectFields.length > 0 && (
                    <div className="row d-flex my-3">
                        <StandartButton
                            variant={ButtonVariant.BLACK_OUTLINED}
                            onClick={addNewRow}
                        >
                            <AddIcon />
                            {rowCount === 0
                                ? (language?.buttons?.add_a_row ?? "Add a Row")
                                : (language?.buttons?.add_new_row ??
                                  "Add New Row")}
                        </StandartButton>
                    </div>
                )}
            </Box>
            {!hideSaveButton && (
                <Box
                    className={
                        props?.isEditMapping &&
                        (props?.closeButtonHidden === undefined ||
                            props?.closeButtonHidden !== true)
                            ? "modal-footer crm-edit-mapping p-0 pt-3 justify-content-between"
                            : "modal-footer crm-edit-mapping p-0 pt-3"
                    }
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                    }}
                >
                    <div className="w-100 d-flex justify-content-end">
                        <StandartButton
                            variant="contained"
                            color="primary"
                            size="small"
                            className="mt-2"
                            isLoading={isSaveLoading}
                            disabled={isSaveLoading}
                            onClick={saveMapping}
                        >
                            {language?.buttons?.save ?? "Save"}
                        </StandartButton>
                    </div>
                </Box>
            )}
        </div>
    );
};

export default CrmSourceMappingArea;
